import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area,
} from 'recharts';
import { financeAPI } from '../services/api';
import StatCard from '../components/StatCard';

const COLORS = ['#3b82f6', '#e2e8f0'];
const AREA_COLORS = ['#6366f1', '#f59e0b'];

const LoadingSkeleton = () => (
  <div className="p-6 space-y-6 animate-pulse" dir="rtl">
    <div className="h-8 bg-gray-200 rounded-lg w-64" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-32 bg-gray-200 rounded-2xl" />
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 h-80 bg-gray-200 rounded-2xl" />
      <div className="h-80 bg-gray-200 rounded-2xl" />
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-xl rounded-xl p-3 border border-gray-100 text-sm" dir="rtl">
      <p className="font-semibold text-gray-700 mb-2">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
          <span className="text-gray-600">{p.name}: <strong>{p.value}</strong></span>
        </div>
      ))}
    </div>
  );
};

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    financeAPI.getDashboard()
      .then((res) => setStats(res.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSkeleton />;

  const occupancyChartData = stats?.occupancyStats?.slice(0, 8).map((t) => ({
    name: t.route.replace('→', '←'),
    محجوز: t.bookedSeats,
    متاح: t.totalSeats - t.bookedSeats,
    ربح: parseFloat(t.netProfit.toFixed(0)),
  })) || [];

  const avgOccupancy = stats?.occupancyStats?.length
    ? (stats.occupancyStats.reduce((s, t) => s + parseFloat(t.occupancyRate), 0) / stats.occupancyStats.length).toFixed(1)
    : 0;

  const todayOccupancy = stats?.occupancyStats?.[0];
  const pieData = todayOccupancy
    ? [
        { name: 'محجوز', value: todayOccupancy.bookedSeats },
        { name: 'متاح', value: todayOccupancy.totalSeats - todayOccupancy.bookedSeats },
      ]
    : [];

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">لوحة التحكم</h1>
          <p className="text-sm text-gray-500 mt-0.5">مرحباً بك، هذه نظرة عامة على أداء شركتك</p>
        </div>
        <div className="text-xs sm:text-sm text-gray-400 bg-white px-3 py-2 rounded-xl border border-gray-200 shadow-sm self-start sm:self-auto">
          {new Date().toLocaleDateString('ar-SA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="إجمالي الحجوزات"
          value={stats?.totalBookings?.toLocaleString() || 0}
          icon="🎫"
          color="blue"
          subtitle="منذ إنشاء الشركة"
        />
        <StatCard
          title="حجوزات اليوم"
          value={stats?.todayBookings || 0}
          icon="📅"
          color="green"
          subtitle="حجز جديد اليوم"
        />
        <StatCard
          title="إيرادات الشهر"
          value={`${(stats?.monthlyRevenue || 0).toLocaleString()} ج`}
          icon="💰"
          color="orange"
          subtitle="إيرادات الشهر الحالي"
        />
        <StatCard
          title="متوسط الإشغال"
          value={`${avgOccupancy}%`}
          icon="📈"
          color="purple"
          subtitle="نسبة الإشغال الإجمالية"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bar chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-base font-semibold text-gray-800">إشغال الرحلات الأخيرة</h2>
              <p className="text-xs text-gray-400 mt-0.5">مقارنة المقاعد المحجوزة والمتاحة</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={occupancyChartData} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="محجوز" fill="#3b82f6" radius={[6, 6, 0, 0]} maxBarSize={32} />
              <Bar dataKey="متاح" fill="#e2e8f0" radius={[6, 6, 0, 0]} maxBarSize={32} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie chart */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-gray-800">نسبة إشغال آخر رحلة</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {todayOccupancy ? todayOccupancy.route : 'لا توجد بيانات'}
            </p>
          </div>
          {pieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    dataKey="value"
                    paddingAngle={3}
                  >
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-6 mt-2">
                {pieData.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i] }} />
                    <span className="text-xs text-gray-600">{d.name}: <strong>{d.value}</strong></span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-300">
              <div className="text-center">
                <div className="text-4xl mb-2">📊</div>
                <p className="text-sm">لا توجد بيانات</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Trips table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-800">تفاصيل الرحلات والأرباح</h2>
            <p className="text-xs text-gray-400 mt-0.5">تحليل مالي لكل رحلة</p>
          </div>
          <span className="text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded-full font-medium">
            {stats?.occupancyStats?.length || 0} رحلة
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-right">
            <thead>
              <tr className="bg-gray-50/80 text-gray-500 text-xs uppercase tracking-wide">
                <th className="px-5 py-3.5 font-medium">المسار</th>
                <th className="px-5 py-3.5 font-medium">التاريخ</th>
                <th className="px-5 py-3.5 font-medium">نسبة الإشغال</th>
                <th className="px-5 py-3.5 font-medium">الإيرادات</th>
                <th className="px-5 py-3.5 font-medium">التكاليف</th>
                <th className="px-5 py-3.5 font-medium">صافي الربح</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {stats?.occupancyStats?.map((t, i) => {
                const rate = parseFloat(t.occupancyRate);
                return (
                  <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-4 font-semibold text-gray-800">{t.route}</td>
                    <td className="px-5 py-4 text-gray-400 text-xs">{t.date}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-100 rounded-full h-1.5 min-w-16">
                          <div
                            className={`h-1.5 rounded-full ${rate >= 80 ? 'bg-green-500' : rate >= 50 ? 'bg-yellow-500' : 'bg-red-400'}`}
                            style={{ width: `${Math.min(rate, 100)}%` }}
                          />
                        </div>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          rate >= 80 ? 'bg-green-50 text-green-700'
                          : rate >= 50 ? 'bg-yellow-50 text-yellow-700'
                          : 'bg-red-50 text-red-600'
                        }`}>
                          {t.occupancyRate}%
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-emerald-600 font-medium">{t.revenue.toLocaleString()} ج</td>
                    <td className="px-5 py-4 text-rose-500">{t.costs.toLocaleString()} ج</td>
                    <td className={`px-5 py-4 font-bold ${t.netProfit >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                      {t.netProfit >= 0 ? '+' : ''}{t.netProfit.toLocaleString()} ج
                    </td>
                  </tr>
                );
              })}
              {!stats?.occupancyStats?.length && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-gray-400">
                    <div className="text-3xl mb-2">📋</div>
                    لا توجد بيانات رحلات بعد
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
